/**
 * Phase 24 / Task 24.1 (DEC-091) — Platform Catalog schema foundation.
 *
 * The platform-catalog inversion documented in docs/PLATFORM_CATALOG.md §2–§8:
 * canonical operator/well identity lives at the PLATFORM level (Paelode-owned,
 * no tenant_id, no RLS, migrator-only writes, all tenants READ); each hauler's
 * working set + per-hauler config lives at the TENANT level via junctions.
 *
 * Tables (9 total — migration 0033_phase24_platform_catalog.sql):
 *   Platform catalogs (NO RLS; INSERT/UPDATE/DELETE REVOKEd from app_user,
 *   mirroring disposal_facilities):
 *     - platform_operators   (spec name `operators` — renamed to avoid colliding
 *                             with the existing tenant-scoped `operators`; the
 *                             canonical rename happens at Task 24.10)
 *     - wells                (one row per API number, globally unique)
 *     - standard_tank_catalog (Paelode-managed tank library; read-only for haulers)
 *   Hauler junctions (tenant-scoped, RLS via tenant_id):
 *     - hauler_operators     (abbreviation + private billing config; XOR platform
 *                             operator vs hauler-private customer)
 *     - tank_batteries       (hauler-created sites; one operator per battery)
 *     - tank_battery_wells   (well↔battery link; one battery per well)
 *     - hauler_tanks         (spec name `site_tanks` — renamed to avoid colliding
 *                             with the existing tenant-scoped `site_tanks`)
 *     - hauler_disposal_facilities (authorized disposal wells)
 *   Audit:
 *     - platform_catalog_audit (tenant-scoped RLS per DEC-091 fix #2 — reads own +
 *                               platform-level rows; writes own-tenant only)
 *
 * ADDITIVE ONLY — the existing operators / company_sites / site_tanks /
 * disposal_facilities tables are untouched here; the dev-data cutover is Task 24.10.
 *
 * Sources: docs/PLATFORM_CATALOG.md §1–§8; master spec §2, §13, §14, §49, §50;
 * gsd/DECISIONS_LOG.md DEC-090 (architecture) + DEC-091 (this task's decisions).
 */
import {
  boolean,
  check,
  foreignKey,
  index,
  integer,
  jsonb,
  numeric,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  type AnyPgColumn,
  unique,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { siteTypes } from './site-types.js';
import { importBatches } from './imports.js';

// ── Platform catalog: platform_operators (no RLS) ────────────────────────────
export const platformOperators = pgTable(
  'platform_operators',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    // Canonical, Paelode-controlled operator name (OCC/RRC-sourced; read-only in the
    // admin console — Paelode overrides the hauler-facing label via display_name).
    name: text('name').notNull(),
    // Phase 25 / Task 25.3 (DEC-114) — Paelode-friendly name shown to haulers instead
    // of the official regulator `name`. Nullable → fall back to `name`.
    displayName: text('display_name'),
    // DEC-110 — Paelode-controlled operator prefix for the HaulId
    // (`{HAULER}-{OPERATOR}{9-digit}`). 2–4 letters, globally unique across the
    // platform catalog; nullable during the transition (generator falls back to
    // hauler_operators.customer_abbreviation when NULL).
    abbreviation: text('abbreviation'),
    // OCC/RRC/WOGCC/NMOCD/NDIC-assigned operator number.
    regulatorOperatorId: text('regulator_operator_id'),
    // 0198 — the agency whose file created this operator (STATE_AGENCIES); NULL for a
    // hand-made one. (regulator, regulator_operator_id) is a live operator's identity.
    regulator: text('regulator'),
    // Primary operating state (OK/TX/WY/NM/ND).
    state: text('state'),
    isProducer: boolean('is_producer').notNull().default(false),
    isDisposal: boolean('is_disposal').notNull().default(false),
    isActive: boolean('is_active').notNull().default(true),
    // Phase 25 / Task 25.3 (DEC-114) — the surviving operator when this row was
    // merged into another (and deactivated). Self-FK.
    mergedIntoId: uuid('merged_into_id').references((): AnyPgColumn => platformOperators.id),
    // OPEN-168 / 0252 — the mailing address and phone from the RRC P-5 file (NULL for OCC and
    // hand-made rows), and the match keys the catalog sync derives from them
    // (catalog-sync/src/rrc/contact.ts). Read by the duplicate matcher.
    contactAddressLine1: text('contact_address_line1'),
    contactAddressLine2: text('contact_address_line2'),
    contactCity: text('contact_city'),
    contactState: text('contact_state'),
    contactZip: text('contact_zip'),
    contactPhone: text('contact_phone'),
    contactPhoneAlt: text('contact_phone_alt'),
    contactAddressKey: text('contact_address_key'),
    contactPhoneKey: text('contact_phone_key'),
    contactPhoneAltKey: text('contact_phone_alt_key'),
    contactUpdatedAt: timestamp('contact_updated_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('platform_operators_abbreviation_key').on(t.abbreviation),
    check(
      'platform_operators_abbreviation_format_check',
      sql`abbreviation IS NULL OR abbreviation ~ '^[A-Za-z]{2,4}$'`,
    ),
    check(
      'platform_operators_regulator_check',
      sql`regulator IS NULL OR regulator IN ('OCC','RRC','WOGCC','NMOCD','NDIC')`,
    ),
    uniqueIndex('platform_operators_regulator_operator_key')
      .on(t.regulator, t.regulatorOperatorId)
      .where(
        sql`regulator IS NOT NULL AND regulator_operator_id IS NOT NULL AND merged_into_id IS NULL`,
      ),
  ],
);

export type PlatformOperator = typeof platformOperators.$inferSelect;
export type NewPlatformOperator = typeof platformOperators.$inferInsert;
export const platformOperatorSelectSchema = createSelectSchema(platformOperators);
export const platformOperatorInsertSchema = createInsertSchema(platformOperators);

// ── Platform catalog: wells (no RLS) ─────────────────────────────────────────
export const wells = pgTable(
  'wells',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    apiNumber: text('api_number').notNull(),
    wellName: text('well_name').notNull(),
    operatorId: uuid('operator_id').references(() => platformOperators.id),
    state: text('state').notNull(),
    county: text('county'),
    // Regulator well status (AC active, PA plugged-and-abandoned, …).
    wellStatus: text('well_status'),
    // OIL / GAS / SWD / INJ / …
    wellType: text('well_type'),
    wellheadLat: numeric('wellhead_lat', { precision: 10, scale: 7 }),
    wellheadLng: numeric('wellhead_lng', { precision: 10, scale: 7 }),
    township: text('township'),
    range: text('range'),
    section: text('section'),
    legalDescription: text('legal_description'),
    source: text('source').default('OCC'),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('wells_api_number_key').on(t.apiNumber),
    index('wells_operator_id_idx').on(t.operatorId),
    index('wells_state_county_idx').on(t.state, t.county),
    index('wells_well_type_idx').on(t.wellType),
    // GIN trigram index on well_name lives in the migration only (not modeled here).
  ],
);

export type Well = typeof wells.$inferSelect;
export type NewWell = typeof wells.$inferInsert;
export const wellSelectSchema = createSelectSchema(wells);
export const wellInsertSchema = createInsertSchema(wells);

// ── Platform catalog: standard_tank_catalog (no RLS) ─────────────────────────
export const standardTankCatalog = pgTable('standard_tank_catalog', {
  id: uuid('id').primaryKey().defaultRandom(),
  // e.g. "400 BBL 12FT".
  name: text('name').notNull(),
  // The nominal rating.
  capacityBbl: numeric('capacity_bbl', { precision: 10, scale: 2 }).notNull(),
  // DEC-379 (0265): the strapped shell, bbl_per_inch × height in inches.
  grossCapacityBbl: numeric('gross_capacity_bbl', { precision: 10, scale: 2 }),
  // Typed from the strapping chart; every gauge-to-barrel conversion uses it. Never computed.
  bblPerInch: numeric('bbl_per_inch', { precision: 8, scale: 4 }).notNull(),
  // DEC-379: optional dimensions, for the BBL/inch sanity warning only (tank-strapping).
  shape: text('shape', { enum: ['ROUND', 'RECTANGULAR'] })
    .notNull()
    .default('ROUND'),
  diameterFt: numeric('diameter_ft', { precision: 8, scale: 4 }),
  lengthFt: numeric('length_ft', { precision: 8, scale: 4 }),
  widthFt: numeric('width_ft', { precision: 8, scale: 4 }),
  heightFt: numeric('height_ft', { precision: 8, scale: 4 }),
  isActive: boolean('is_active').notNull().default(true),
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
});

export type StandardTank = typeof standardTankCatalog.$inferSelect;
export type NewStandardTank = typeof standardTankCatalog.$inferInsert;
export const standardTankSelectSchema = createSelectSchema(standardTankCatalog);
export const standardTankInsertSchema = createInsertSchema(standardTankCatalog);

// ── operator_hauler_access (platform-managed, no RLS) — migration 0206 ─────────
// Operator portal PR 3 (design §1.5): Paelode confirms which haulers' data an operator sees.
// The operator_portal_* scope functions require an ACTIVE row (and the hauler's customer mapping,
// as before). Keyed on the operator's chain head; app_user may only read it: writes go through
// platform_set_operator_hauler_access (SECURITY DEFINER).
export const operatorHaulerAccess = pgTable(
  'operator_hauler_access',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    platformOperatorId: uuid('platform_operator_id')
      .notNull()
      .references(() => platformOperators.id, { onDelete: 'cascade' }),
    haulerTenantId: uuid('hauler_tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /** ACTIVE | REVOKED. A revoked row is kept: who and when. */
    status: text('status').notNull().default('ACTIVE'),
    /** MANUAL | SUGGESTION | BACKFILL. */
    source: text('source').notNull(),
    confirmedByUserId: uuid('confirmed_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    confirmedAt: timestamp('confirmed_at', { withTimezone: true }).notNull().defaultNow(),
    revokedByUserId: uuid('revoked_by_user_id').references(() => users.id, {
      onDelete: 'set null',
    }),
    revokedAt: timestamp('revoked_at', { withTimezone: true }),
    note: text('note'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('operator_hauler_access_operator_hauler_key').on(t.platformOperatorId, t.haulerTenantId),
    index('operator_hauler_access_hauler_idx').on(t.haulerTenantId),
    check('operator_hauler_access_status_check', sql`status IN ('ACTIVE', 'REVOKED')`),
    check(
      'operator_hauler_access_source_check',
      sql`source IN ('MANUAL', 'SUGGESTION', 'BACKFILL')`,
    ),
    check(
      'operator_hauler_access_revoked_check',
      sql`(status = 'REVOKED' AND revoked_at IS NOT NULL) OR (status = 'ACTIVE' AND revoked_at IS NULL AND revoked_by_user_id IS NULL)`,
    ),
  ],
);

// ── Junction: hauler_operators (tenant-scoped, RLS) ──────────────────────────
export const haulerOperators = pgTable(
  'hauler_operators',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // NULL when this is a hauler-private customer (is_platform_operator = false).
    operatorId: uuid('operator_id').references(() => platformOperators.id),
    isPlatformOperator: boolean('is_platform_operator').notNull().default(true),
    // Used when operator_id is NULL — drilling companies, private landowners, etc.
    customerName: text('customer_name'),
    // 2-4 letters — drives the HaulId prefix (Task 24.4).
    customerAbbreviation: text('customer_abbreviation').notNull(),
    billingContactName: text('billing_contact_name'),
    billingContactEmail: text('billing_contact_email'),
    billingContactPhone: text('billing_contact_phone'),
    billingAddress: text('billing_address'),
    // DEC-192 option A (migration 0083) — per-customer rate overrides, mirroring the
    // same three columns on `operators`. THIS is the table billing resolves through for
    // every job the product creates today (which sets hauler_operator_id and leaves
    // jobs.operator_id NULL); the `operators` copies remain for legacy jobs. Nullable =
    // fall back to the legacy operator rate, then the job-type default. CHECK
    // non-negative, as on `operators`.
    perBblPriceCents: integer('per_bbl_price_cents'),
    hourlyPriceCents: integer('hourly_price_cents'),
    bidPriceCents: integer('bid_price_cents'),
    // Migration 0086 — the per-customer billing CLOCK policy, the piece 0083 left on
    // `operators`. Decides when an HOURLY meter starts and stops, so it moves money:
    // ARRIVAL vs START and DEPARTURE vs COMPLETION change billable hours directly.
    // NULL = inherit, resolving customer → operator → tenant exactly as the rates do.
    billingStart: text('billing_start'),
    billingEnd: text('billing_end'),
    // Migration 0142 (DEC-326) — payment terms in days for this customer's invoices.
    // NULL = the company default (hauler_tenant_invoice_settings, Net 30).
    paymentTermsDays: integer('payment_terms_days'),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('hauler_operators_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'hauler_operators_import_batch_tenant_fk',
    }).onDelete('set null'),
    unique('hauler_operators_tenant_abbrev_key').on(t.tenantId, t.customerAbbreviation),
    index('hauler_operators_tenant_idx').on(t.tenantId),
    index('hauler_operators_operator_id_idx').on(t.operatorId),
    check('hauler_operators_abbrev_format_check', sql`customer_abbreviation ~ '^[A-Za-z]{2,4}$'`),
    check(
      'hauler_operators_platform_xor_customer_check',
      sql`(operator_id IS NOT NULL AND is_platform_operator = true)
          OR (operator_id IS NULL AND is_platform_operator = false AND customer_name IS NOT NULL)`,
    ),
    // Migration 0083 — mirrors the non-negative CHECKs on `operators`.
    check(
      'hauler_operators_per_bbl_price_nonneg',
      sql`per_bbl_price_cents IS NULL OR per_bbl_price_cents >= 0`,
    ),
    check(
      'hauler_operators_hourly_price_nonneg',
      sql`hourly_price_cents IS NULL OR hourly_price_cents >= 0`,
    ),
    check(
      'hauler_operators_bid_price_nonneg',
      sql`bid_price_cents IS NULL OR bid_price_cents >= 0`,
    ),
    // Migration 0086 — same allowlists as the `operators` copies (DEC-032 #1), so a
    // value that is valid on one table is valid on the other and the fallback chain
    // never has to translate between them.
    check(
      'hauler_operators_billing_start_check',
      sql`billing_start IS NULL OR billing_start IN ('ARRIVAL','START')`,
    ),
    check(
      'hauler_operators_billing_end_check',
      sql`billing_end IS NULL OR billing_end IN ('DEPARTURE','COMPLETION')`,
    ),
    check(
      'hauler_operators_payment_terms_check',
      sql`payment_terms_days IS NULL OR payment_terms_days BETWEEN 0 AND 365`,
    ),
    pgPolicy('hauler_operators_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type HaulerOperator = typeof haulerOperators.$inferSelect;
export type NewHaulerOperator = typeof haulerOperators.$inferInsert;
export const haulerOperatorSelectSchema = createSelectSchema(haulerOperators);
export const haulerOperatorInsertSchema = createInsertSchema(haulerOperators);

// ── hauler_locations (tenant-scoped, RLS) — the unified hauler location model ─
// Phase 26 / DEC-147 — renamed from `tank_batteries`. Holds EVERY hauler-created
// location, typed via `site_type_id` (Tank Battery / Compressor Station / Rig / …).
// Well-linking (`hauler_location_wells`) is TANK_BATTERY-exclusive; pickup/dropoff
// capability is per-location (`can_pickup`/`can_dropoff`). NOTE: the API contract
// still exposes these as "tank battery" (route /v1/tank-batteries, response field
// `tankBatteryId`) until Task 26.5 (web) / 27 (mobile) — Task 26.1 is a
// backend-internal rename only.
export const haulerLocations = pgTable(
  'hauler_locations',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    name: text('name').notNull(),
    haulerOperatorId: uuid('hauler_operator_id')
      .notNull()
      .references(() => haulerOperators.id),
    // Phase 26 / DEC-147 — site typing (extends DEC-033). Nullable FK to the
    // platform-managed site_types catalog; backfilled/seeded to TANK_BATTERY.
    siteTypeId: uuid('site_type_id').references(() => siteTypes.id, { onDelete: 'restrict' }),
    // Phase 26 / DEC-147 — per-location pickup/dropoff capability (independent flags).
    // Defaults match Tank Battery/Rig/Compressor Station/Other (pickup=true,
    // dropoff=false); overridable per location. OCC disposal_facilities (separate
    // table) are dropoff-only and never carry these flags.
    canPickup: boolean('can_pickup').notNull().default(true),
    canDropoff: boolean('can_dropoff').notNull().default(false),
    // true = private site, no platform well connection.
    isPrivate: boolean('is_private').notNull().default(false),
    // Phase 24 / Task 24.6 (DEC-097) — geofence pin center (the editor's RADIUS
    // "Verify & Save" writes here). Defaults to the avg of linked-well coords when
    // unset (computed in GET /v1/sites). site_geofences is keyed by site_id =
    // hauler_location_id + site_type='TANK_BATTERY'.
    centerGpsLat: numeric('center_gps_lat', { precision: 10, scale: 7 }),
    centerGpsLng: numeric('center_gps_lng', { precision: 10, scale: 7 }),
    // DEC-310 / migration 0125 — county, filled from GPS by the API
    // (`services/site-county.ts`). All three set together or all NULL (CHECK).
    // `county` is the bare upper-case name (GARVIN); `county_state` is needed beside
    // it because this table has no state and county names repeat across states.
    // `county_source` USER (typed, always wins) | DERIVED (worked out by the API).
    county: text('county'),
    countyState: text('county_state'),
    countySource: text('county_source').$type<'USER' | 'DERIVED'>(),
    // Phase 13.4a (0236; Corbin) — auto-haul is switched on per site, off by default.
    autoHaulEnabled: boolean('auto_haul_enabled').notNull().default(false),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('hauler_locations_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'hauler_locations_import_batch_tenant_fk',
    }).onDelete('set null'),
    index('hauler_locations_tenant_idx').on(t.tenantId),
    index('hauler_locations_hauler_operator_idx').on(t.haulerOperatorId),
    // DEC-310 / migration 0125 (authoritative).
    check('hauler_locations_county_source_check', sql`county_source IN ('USER', 'DERIVED')`),
    check('hauler_locations_county_state_shape_check', sql`county_state ~ '^[A-Z]{2}$'`),
    check(
      'hauler_locations_county_upper_check',
      sql`county = upper(county) AND county = btrim(county) AND county <> ''`,
    ),
    check(
      'hauler_locations_county_all_or_none_check',
      sql`(county IS NULL AND county_state IS NULL AND county_source IS NULL) OR (county IS NOT NULL AND county_state IS NOT NULL AND county_source IS NOT NULL)`,
    ),
    pgPolicy('hauler_locations_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type HaulerLocation = typeof haulerLocations.$inferSelect;
export type NewHaulerLocation = typeof haulerLocations.$inferInsert;
export const haulerLocationSelectSchema = createSelectSchema(haulerLocations);
export const haulerLocationInsertSchema = createInsertSchema(haulerLocations);

// ── hauler_location_wells (well↔location link, RLS) — renamed from tank_battery_wells ─
// Phase 26 / DEC-147. Well-linking is TANK_BATTERY-exclusive (DEC-147 #4); this table
// links wells to a hauler_location of site_type=TANK_BATTERY. UNIQUE(tenant_id, well_id)
// — hard block "one well, one location PER HAULER" (DEC-091 fix #3 — was a global
// UNIQUE(well_id) that would have blocked two haulers from each linking the same shared
// platform well to their own location).
export const haulerLocationWells = pgTable(
  'hauler_location_wells',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    haulerLocationId: uuid('hauler_location_id')
      .notNull()
      .references(() => haulerLocations.id, { onDelete: 'cascade' }),
    wellId: uuid('well_id')
      .notNull()
      .references(() => wells.id),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    linkedAt: timestamp('linked_at', { withTimezone: true }).notNull().defaultNow(),
    linkedBy: uuid('linked_by').references(() => users.id, { onDelete: 'set null' }),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('hauler_location_wells_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'hauler_location_wells_import_batch_tenant_fk',
    }).onDelete('set null'),
    unique('hauler_location_wells_tenant_well_key').on(t.tenantId, t.wellId),
    index('hauler_location_wells_location_idx').on(t.haulerLocationId),
    index('hauler_location_wells_tenant_idx').on(t.tenantId),
    pgPolicy('hauler_location_wells_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type HaulerLocationWell = typeof haulerLocationWells.$inferSelect;
export type NewHaulerLocationWell = typeof haulerLocationWells.$inferInsert;
export const haulerLocationWellSelectSchema = createSelectSchema(haulerLocationWells);
export const haulerLocationWellInsertSchema = createInsertSchema(haulerLocationWells);

// ── hauler_tanks (hauler-configured tanks; spec `site_tanks`, RLS) ────────────
// XOR: a tank belongs to either a hauler_location OR a well directly, never both.
export const haulerTanks = pgTable(
  'hauler_tanks',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    haulerLocationId: uuid('hauler_location_id').references(() => haulerLocations.id),
    wellId: uuid('well_id').references(() => wells.id),
    standardTankCatalogId: uuid('standard_tank_catalog_id').references(
      () => standardTankCatalog.id,
    ),
    name: text('name').notNull(),
    capacityBbl: numeric('capacity_bbl', { precision: 10, scale: 2 }),
    bblPerInch: numeric('bbl_per_inch', { precision: 8, scale: 4 }),
    tankType: text('tank_type'),
    // What the producer calls this tank.
    producerIdentifier: text('producer_identifier'),
    // Phase 13.4a (0236) — the haul threshold: the % full that needs a haul (NULL = none), how
    // many hours ahead to act on a predicted crossing, and the job type an auto job gets (13.4b's
    // sweeper; NULL = no auto job for this tank).
    thresholdPercent: numeric('threshold_percent', { precision: 5, scale: 2 }),
    leadTimeHours: integer('lead_time_hours').notNull().default(24),
    autoHaulJobTypeFamilyId: uuid('auto_haul_job_type_family_id'),
    // Phase 13.4b (0237) — the sweeper found the tank due but would not create a job, and why
    // (hauler_tanks_auto_haul_hold_reason_check); NULL = not held.
    autoHaulHoldReason: text('auto_haul_hold_reason'),
    autoHaulHoldAt: timestamp('auto_haul_hold_at', { withTimezone: true }),
    // 0237 — consecutive sweeps that failed on this tank; the third holds it as ERROR.
    autoHaulFailures: integer('auto_haul_failures').notNull().default(0),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('hauler_tanks_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'hauler_tanks_import_batch_tenant_fk',
    }).onDelete('set null'),
    index('hauler_tanks_tenant_idx').on(t.tenantId),
    index('hauler_tanks_location_idx').on(t.haulerLocationId),
    index('hauler_tanks_well_idx').on(t.wellId),
    check(
      'hauler_tanks_location_xor_well_check',
      sql`(hauler_location_id IS NOT NULL AND well_id IS NULL)
          OR (hauler_location_id IS NULL AND well_id IS NOT NULL)`,
    ),
    pgPolicy('hauler_tanks_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type HaulerTank = typeof haulerTanks.$inferSelect;
export type NewHaulerTank = typeof haulerTanks.$inferInsert;
export const haulerTankSelectSchema = createSelectSchema(haulerTanks);
export const haulerTankInsertSchema = createInsertSchema(haulerTanks);

// ── hauler_wells ("this hauler works with this well", RLS) ───────────────────
// Task 24.11 (DEC-105) — catalog membership WITHOUT requiring a tank. A bulk
// import with no tank template adds hauler_wells rows only; adding a standalone
// well tank also upserts a hauler_wells row (a tank implies membership). Mutually
// exclusive with tank_battery_wells (a battery well is not a hauler_wells well).
export const haulerWells = pgTable(
  'hauler_wells',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    wellId: uuid('well_id')
      .notNull()
      .references(() => wells.id),
    // Phase 13.4a (0236; Corbin) — auto-haul for this hauler's standalone tanks on this well,
    // off by default (a standalone well tank's site is the well).
    autoHaulEnabled: boolean('auto_haul_enabled').notNull().default(false),
    isActive: boolean('is_active').notNull().default(true),
    addedBy: uuid('added_by').references(() => users.id, { onDelete: 'set null' }),
    addedAt: timestamp('added_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('hauler_wells_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'hauler_wells_import_batch_tenant_fk',
    }).onDelete('set null'),
    unique('hauler_wells_tenant_well_key').on(t.tenantId, t.wellId),
    index('hauler_wells_tenant_idx').on(t.tenantId),
    index('hauler_wells_well_idx').on(t.wellId),
    pgPolicy('hauler_wells_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type HaulerWell = typeof haulerWells.$inferSelect;
export type NewHaulerWell = typeof haulerWells.$inferInsert;
export const haulerWellSelectSchema = createSelectSchema(haulerWells);
export const haulerWellInsertSchema = createInsertSchema(haulerWells);

// ── hauler_disposal_facilities (authorized disposal wells, RLS) ──────────────
// well_id must be a well with well_type SWD/INJ — enforced at the app layer (Task 24.6).
export const haulerDisposalFacilities = pgTable(
  'hauler_disposal_facilities',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    wellId: uuid('well_id')
      .notNull()
      .references(() => wells.id),
    // Hauler's nickname for this facility.
    displayName: text('display_name'),
    isActive: boolean('is_active').notNull().default(true),
    authorizedBy: uuid('authorized_by').references(() => users.id, { onDelete: 'set null' }),
    authorizedAt: timestamp('authorized_at', { withTimezone: true }).notNull().defaultNow(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('hauler_disposal_facilities_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'hauler_disposal_facilities_import_batch_tenant_fk',
    }).onDelete('set null'),
    unique('hauler_disposal_facilities_tenant_well_key').on(t.tenantId, t.wellId),
    index('hauler_disposal_facilities_tenant_idx').on(t.tenantId),
    index('hauler_disposal_facilities_well_idx').on(t.wellId),
    pgPolicy('hauler_disposal_facilities_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type HaulerDisposalFacility = typeof haulerDisposalFacilities.$inferSelect;
export type NewHaulerDisposalFacility = typeof haulerDisposalFacilities.$inferInsert;
export const haulerDisposalFacilitySelectSchema = createSelectSchema(haulerDisposalFacilities);
export const haulerDisposalFacilityInsertSchema = createInsertSchema(haulerDisposalFacilities);

// ── platform_catalog_audit (catalog change log, RLS) ─────────────────────────
// DEC-091 fix #2 — READ: own-tenant rows OR platform-level (tenant_id NULL) rows.
// WRITE: own-tenant only (WITH CHECK) so app_user can never forge a platform-level
// audit row. "Platform-level → PLATFORM_ADMIN only" is gated at the app role-guard
// layer (Task 24.6); migrator/PLATFORM_ADMIN sees all via BYPASSRLS. tenant_id NULL
// = platform-level change.
export const platformCatalogAudit = pgTable(
  'platform_catalog_audit',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id').references(() => tenants.id, { onDelete: 'set null' }),
    entityType: text('entity_type').notNull(),
    entityId: uuid('entity_id').notNull(),
    action: text('action').notNull(),
    changedBy: uuid('changed_by').references(() => users.id, { onDelete: 'set null' }),
    changedAt: timestamp('changed_at', { withTimezone: true }).notNull().defaultNow(),
    oldValues: jsonb('old_values'),
    newValues: jsonb('new_values'),
    notes: text('notes'),
  },
  (t) => [
    index('platform_catalog_audit_tenant_idx').on(t.tenantId),
    index('platform_catalog_audit_entity_idx').on(t.entityType, t.entityId),
    check(
      'platform_catalog_audit_entity_type_check',
      // Phase 25 — 'TENANT' (Task 25.2, 0044) + 'SYNC_CONFIG' (Task 25.5, 0047) for admin audit.
      sql`entity_type IN ('OPERATOR','WELL','TANK_BATTERY','TANK','DISPOSAL_AUTH','HAULER_OPERATOR','TENANT','SYNC_CONFIG')`,
    ),
    check(
      'platform_catalog_audit_action_check',
      sql`action IN ('CREATED','UPDATED','DEACTIVATED','REACTIVATED','WELL_LINKED','WELL_UNLINKED','TANK_MOVED')`,
    ),
    pgPolicy('platform_catalog_audit_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid OR tenant_id IS NULL`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type PlatformCatalogAudit = typeof platformCatalogAudit.$inferSelect;
export type NewPlatformCatalogAudit = typeof platformCatalogAudit.$inferInsert;
export const platformCatalogAuditSelectSchema = createSelectSchema(platformCatalogAudit);
export const platformCatalogAuditInsertSchema = createInsertSchema(platformCatalogAudit);

// ── hauler_sequence_counters (per hauler+operator HaulId counter, RLS) ────────
// Phase 24 / Task 24.4 (DEC-095) — the new HaulId format is
// `{HAULER_ABBREV}-{OPERATOR_ABBREV}{9-digit}`, keyed by the (tenant, hauler_operator)
// junction so two haulers hauling for the same platform operator keep independent,
// non-colliding sequences. Advanced atomically via `INSERT … ON CONFLICT
// (tenant_id, hauler_operator_id) DO UPDATE SET last_sequence = last_sequence + 1
// RETURNING` (row-lock atomic — same proven pattern as operator_haul_id_counters).
export const haulerSequenceCounters = pgTable(
  'hauler_sequence_counters',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    haulerOperatorId: uuid('hauler_operator_id')
      .notNull()
      .references(() => haulerOperators.id, { onDelete: 'cascade' }),
    // INTEGER is sufficient for a 9-digit sequence (max 999,999,999 < 2^31-1).
    lastSequence: integer('last_sequence').notNull().default(0),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('hauler_sequence_counters_tenant_operator_key').on(t.tenantId, t.haulerOperatorId),
    index('hauler_sequence_counters_tenant_idx').on(t.tenantId),
    pgPolicy('hauler_sequence_counters_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type HaulerSequenceCounter = typeof haulerSequenceCounters.$inferSelect;
export type NewHaulerSequenceCounter = typeof haulerSequenceCounters.$inferInsert;
export const haulerSequenceCounterSelectSchema = createSelectSchema(haulerSequenceCounters);
export const haulerSequenceCounterInsertSchema = createInsertSchema(haulerSequenceCounters);
